import { autorun } from 'mobx'
import { useEffect, useRef } from 'react'

import type { EscapeDismissible } from '@/utils/escapeDismissStack'
import { popEscapeDismiss, pushEscapeDismiss } from '@/utils/escapeDismissStack'

// 스택에서 빼려면 entry 의 identity 가 유지돼야 하므로 ref 로 고정하고,
// close 만 최신 렌더의 것으로 갈아끼운다.
const useDismissEntry = (close: () => void) => {
  const closeRef = useRef(close)
  closeRef.current = close

  const entryRef = useRef<EscapeDismissible>({ close: () => closeRef.current() })
  return entryRef.current
}

/** 열려 있는 동안 Escape 스택에 참여한다. isOpen 이 일반 React 상태인 경우. */
export const useEscapeDismiss = (isOpen: boolean, close: () => void) => {
  const entry = useDismissEntry(close)

  useEffect(() => {
    if (!isOpen) return

    pushEscapeDismiss(entry)
    return () => popEscapeDismiss(entry)
  }, [isOpen, entry])
}

/**
 * isOpen 이 MobX observable 인 경우. 렌더 사이클로 관찰할 수 없는 컴포넌트
 * (예: 본문 밖에서 `<Observer>` 안에서만 isOpen 을 읽는 컴포넌트)를 위해 autorun 으로 추적한다.
 */
export const useEscapeDismissObservable = (target: EscapeDismissible & { isOpen: boolean }) => {
  const entry = useDismissEntry(() => target.close())

  useEffect(() => {
    let registered = false

    const dispose = autorun(() => {
      const { isOpen } = target
      if (isOpen === registered) return

      if (isOpen) pushEscapeDismiss(entry)
      else popEscapeDismiss(entry)
      registered = isOpen
    })

    return () => {
      dispose()
      if (registered) popEscapeDismiss(entry)
    }
  }, [target, entry])
}
